/**
 * shareWithIntro — single source of truth for the "share from Kinly"
 * gesture across all surfaces (lists, events, chores, to-dos).
 *
 * Every share gets a friendly intro line that doubles as soft marketing:
 *   "Hey, here's my Kinly grocery list — …"
 *   "Hey, look at this event in Kinly — …"
 *
 * The user can delete the intro before sending if they want a clean
 * paste — Web Share / clipboard both surface the full text as editable.
 *
 * Returns a Promise<{ok, used}> where used is one of "share" | "clipboard".
 * Never throws; the user cancelling a share is not an error.
 */
const APP_TAG = "— sent from Kinly";

function _bullet(s) {
  return s ? `• ${s}` : null;
}

function _line(...parts) {
  return parts.filter(Boolean).join(" ");
}

function _fmtWhen(iso, allDay) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (allDay) {
      return d.toLocaleDateString(undefined, {
        weekday: "long", day: "numeric", month: "long",
      });
    }
    return d.toLocaleString(undefined, {
      weekday: "short", day: "numeric", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return null; }
}

function buildListBody({ list, items = [] }) {
  const name = list?.name || "Shopping list";
  const intro = `Hey, here's my Kinly ${name.toLowerCase()} list —`;
  const lines = [];
  // For grocery / checklist-style lists we want EVERY ticked + unticked
  // sub-item so the recipient sees what's actually being asked for.
  let anyContent = false;
  for (const it of items) {
    const sub = it.checklist || [];
    if (sub.length) {
      for (const s of sub) {
        if (s.done) continue; // skip already-done items
        lines.push(_bullet(s.title));
        anyContent = true;
      }
    } else if (!it.done) {
      lines.push(_bullet(it.title));
      anyContent = true;
    }
  }
  if (!anyContent) lines.push("(empty list — nothing left to grab.)");
  return [intro, ...lines, "", APP_TAG].join("\n");
}

function buildEventBody({ event, members = [] }) {
  const title = event?.title || "Event";
  const intro = `Hey, look at this event in Kinly —`;
  const when = _fmtWhen(event?.start_at, event?.all_day);
  const lines = [
    `${title}`,
    when && `When: ${when}`,
    event?.location_name && `Where: ${event.location_name}`,
    event?.notes && `Notes: ${event.notes}`,
  ].filter(Boolean);
  if (event?.participant_person_ids?.length && members?.length) {
    const names = event.participant_person_ids
      .map((id) => members.find((m) => m.id === id)?.display_name)
      .filter(Boolean)
      .join(", ");
    if (names) lines.push(`With: ${names}`);
  }
  return [intro, "", ...lines, "", APP_TAG].join("\n");
}

function buildChoreBody({ chore, members = [] }) {
  const intro = `Hey, here's a chore from Kinly —`;
  const assigneeIds = chore?.assignee_person_ids || [];
  const assignees = assigneeIds
    .map((id) => members.find((m) => m.id === id)?.display_name)
    .filter(Boolean)
    .join(", ");
  const due = _fmtWhen(chore?.due_at, false);
  const lines = [
    chore?.title,
    assignees && `For: ${assignees}`,
    due && `Due: ${due}`,
    chore?.reward?.amount && `Reward: ${chore.reward.amount}★`,
  ].filter(Boolean);
  return [intro, "", ...lines, "", APP_TAG].join("\n");
}

function buildTodoBody({ todo, listName }) {
  const intro = `Hey, here's a to-do from Kinly —`;
  const lines = [
    todo?.title,
    listName && `List: ${listName}`,
    todo?.due_at && `Due: ${_fmtWhen(todo.due_at, false)}`,
  ].filter(Boolean);
  return [intro, "", ...lines, "", APP_TAG].join("\n");
}

/**
 * Main entry. Pick a `kind` and supply the right payload.
 *
 *   shareWithIntro({ kind: "list",  list, items })
 *   shareWithIntro({ kind: "event", event, members })
 *   shareWithIntro({ kind: "chore", chore, members })
 *   shareWithIntro({ kind: "todo",  todo, listName })
 */
export async function shareWithIntro(payload) {
  let body = "";
  switch (payload.kind) {
    case "list":  body = buildListBody(payload); break;
    case "event": body = buildEventBody(payload); break;
    case "chore": body = buildChoreBody(payload); break;
    case "todo":  body = buildTodoBody(payload); break;
    default:      body = String(payload?.text || "Shared from Kinly");
  }
  try {
    if (typeof navigator !== "undefined" && navigator.share) {
      await navigator.share({ title: "Kinly", text: body });
      return { ok: true, used: "share" };
    }
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      await navigator.clipboard.writeText(body);
      return { ok: true, used: "clipboard" };
    }
    return { ok: false, used: "none" };
  } catch {
    // User dismissed the share sheet, or Web Share rejected — treat as cancel.
    return { ok: false, used: "cancelled" };
  }
}
